import axiosInstance from '../axiosConfig';
import {
  KidsClubBooking,
  KidsClubBookingFormData,
  KidsClubCapacity,
  KidsClubCheckIn,
  KidsClubCheckInFormData,
  KidsClubCheckOutFormData,
  EmergencyAlert,
  EmergencyAlertFormData,
  CapacityQueryParams,
  KidsClubBookingResponse,
  KidsClubBookingsResponse,
  KidsClubCapacityResponse,
  KidsClubCheckInResponse,
  EmergencyAlertResponse,
  WalkInFormData,
  KidsClubSettings,
  CapacityOverride,
  BookingSummary,
  MemberLookupResult,
} from '../../types';

/**
 * Kids Club Service
 * Handles all Kids Club-related API calls
 */
class KidsClubService {
  private baseUrl = '/kids-club';

  // ==========================================
  // Member Booking Endpoints
  // ==========================================

  /**
   * Create a new Kids Club booking
   * @param data - Booking form data
   * @returns Created booking
   */
  async createBooking(data: KidsClubBookingFormData): Promise<KidsClubBooking> {
    const response = await axiosInstance.post<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings`,
      data
    );
    return response.data.data;
  }

  /**
   * Get current member's bookings
   * @returns List of bookings
   */
  async getMyBookings(): Promise<KidsClubBooking[]> {
    const response = await axiosInstance.get<KidsClubBookingsResponse>(
      `${this.baseUrl}/bookings/me`
    );
    return response.data.data;
  }

  /**
   * Get upcoming bookings for current member
   * @param daysAhead - Number of days ahead to query (default 7)
   * @returns List of upcoming bookings
   */
  async getUpcomingBookings(daysAhead: number = 7): Promise<KidsClubBooking[]> {
    const response = await axiosInstance.get<KidsClubBookingsResponse>(
      `${this.baseUrl}/bookings/upcoming`,
      {
        params: { days_ahead: daysAhead },
      }
    );
    return response.data.data;
  }

  /**
   * Cancel a booking
   * @param bookingId - Booking ID
   * @param reason - Cancellation reason
   * @returns Updated booking
   */
  async cancelBooking(bookingId: string, reason?: string): Promise<KidsClubBooking> {
    const response = await axiosInstance.post<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings/${bookingId}/cancel`,
      { reason }
    );
    return response.data.data;
  }

  /**
   * Confirm a pending booking (Staff only)
   * @param bookingId - Booking ID
   * @returns Updated booking
   */
  async confirmBooking(bookingId: string): Promise<KidsClubBooking> {
    const response = await axiosInstance.post<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings/${bookingId}/confirm`
    );
    return response.data.data;
  }

  /**
   * Reject a pending booking (Staff only)
   * @param bookingId - Booking ID
   * @param rejectionReason - Reason for rejection
   * @returns Updated booking (cancelled)
   */
  async rejectBooking(bookingId: string, rejectionReason: string): Promise<KidsClubBooking> {
    const response = await axiosInstance.post<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings/${bookingId}/reject`,
      { rejection_reason: rejectionReason }
    );
    return response.data.data;
  }

  /**
   * Cancel a booking as staff (Staff only)
   * Allows staff to cancel any booking in any status for administrative reasons
   * @param bookingId - Booking ID
   * @param cancellationReason - Reason for cancellation
   * @returns Updated booking (cancelled)
   */
  async staffCancelBooking(bookingId: string, cancellationReason: string): Promise<KidsClubBooking> {
    const response = await axiosInstance.post<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings/${bookingId}/staff-cancel`,
      { cancellation_reason: cancellationReason }
    );
    return response.data.data;
  }

  /**
   * Reinstate a cancelled booking (Staff only)
   * Restores a cancelled booking back to confirmed status
   * @param bookingId - Booking ID
   * @returns Updated booking (confirmed)
   */
  async reinstateBooking(bookingId: string): Promise<KidsClubBooking> {
    const response = await axiosInstance.post<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings/${bookingId}/reinstate`
    );
    return response.data.data;
  }

  /**
   * Update an existing booking (Staff only)
   * Allows staff to modify booking details including date, time, session, and notes
   * @param bookingId - Booking ID
   * @param data - Fields to update
   * @returns Updated booking
   */
  async updateBooking(bookingId: string, data: {
    booking_date?: string;
    start_time?: string;
    end_time?: string;
    session?: string;
    parent_location?: string;
    special_instructions?: string;
  }): Promise<KidsClubBooking> {
    const response = await axiosInstance.put<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings/${bookingId}`,
      data
    );
    return response.data.data;
  }

  /**
   * Create a staff booking without time restrictions (Staff only)
   * Allows same-day bookings and auto-confirms
   * @param data - Booking form data
   * @returns Created booking (auto-confirmed)
   */
  async createStaffBooking(data: KidsClubBookingFormData): Promise<KidsClubBooking> {
    const response = await axiosInstance.post<KidsClubBookingResponse>(
      `${this.baseUrl}/bookings/staff`,
      data
    );
    return response.data.data;
  }

  // ==========================================
  // Capacity Endpoints
  // ==========================================

  /**
   * Get available capacity slots
   * @param params - Query parameters (date, min_spots, etc.)
   * @returns List of available capacity slots
   */
  async getAvailableCapacity(params: CapacityQueryParams): Promise<KidsClubCapacity[]> {
    const response = await axiosInstance.get<KidsClubCapacityResponse>(
      `${this.baseUrl}/capacity/available`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Get capacity for a date range
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @returns List of capacity slots in range
   */
  async getCapacityRange(startDate: string, endDate: string): Promise<KidsClubCapacity[]> {
    const response = await axiosInstance.get<KidsClubCapacityResponse>(
      `${this.baseUrl}/capacity/range`,
      {
        params: { start_date: startDate, end_date: endDate },
      }
    );
    return response.data.data;
  }

  /**
   * Get capacity calendar for members (Member-facing view)
   * Returns availability info without other members' personal details
   * @param startDate - Start date (YYYY-MM-DD) - defaults to today
   * @param endDate - End date (YYYY-MM-DD) - defaults to start + 14 days
   * @returns Calendar with availability and member's own bookings
   */
  async getMemberCapacityCalendar(startDate?: string, endDate?: string): Promise<any> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await axiosInstance.get<{ success: boolean; data: any }>(
      `${this.baseUrl}/capacity/calendar`,
      { params }
    );
    return response.data.data;
  }

  // ==========================================
  // Staff Check-In Endpoints
  // ==========================================

  /**
   * Get today's bookings (Staff only)
   * @param status - Status filter (default: confirmed)
   * @returns List of today's bookings
   */
  async getTodaysBookings(status: string = 'confirmed'): Promise<KidsClubBooking[]> {
    const response = await axiosInstance.get<KidsClubBookingsResponse>(
      `${this.baseUrl}/bookings/today`,
      { params: { status } }
    );
    return response.data.data;
  }

  /**
   * Get all pending bookings across all members (Staff only)
   * @param daysAhead - Number of days to look ahead (default: 7)
   * @returns List of pending bookings
   */
  async getStaffPendingBookings(daysAhead: number = 7): Promise<KidsClubBooking[]> {
    const response = await axiosInstance.get<KidsClubBookingsResponse>(
      `${this.baseUrl}/bookings/staff-pending`,
      { params: { days_ahead: daysAhead } }
    );
    return response.data.data;
  }

  /**
   * Perform check-in for a booking (Staff only)
   * @param data - Check-in form data
   * @returns Created check-in record
   */
  async performCheckIn(data: KidsClubCheckInFormData): Promise<KidsClubCheckIn> {
    const response = await axiosInstance.post<KidsClubCheckInResponse>(
      `${this.baseUrl}/check-ins`,
      data
    );
    return response.data.data;
  }

  /**
   * Perform check-out for a booking (Staff only)
   * @param checkInId - Check-in ID
   * @param data - Check-out form data
   * @returns Updated check-in record
   */
  async performCheckOut(
    checkInId: string,
    data: KidsClubCheckOutFormData
  ): Promise<KidsClubCheckIn> {
    const response = await axiosInstance.post<KidsClubCheckInResponse>(
      `${this.baseUrl}/check-ins/${checkInId}/check-out`,
      data
    );
    return response.data.data;
  }

  /**
   * Get current check-ins (Staff only)
   * @returns List of current check-ins
   */
  async getCurrentCheckIns(): Promise<KidsClubCheckIn[]> {
    const response = await axiosInstance.get<{ success: boolean; data: KidsClubCheckIn[] }>(
      `${this.baseUrl}/check-ins/current`
    );
    return response.data.data;
  }

  /**
   * Get today's check-ins (both checked in and checked out) (Staff only)
   * @returns List of today's check-ins
   */
  async getTodaysCheckIns(): Promise<KidsClubCheckIn[]> {
    const response = await axiosInstance.get<{ success: boolean; data: KidsClubCheckIn[] }>(
      `${this.baseUrl}/check-ins/today`
    );
    return response.data.data;
  }

  /**
   * Update parent location (Staff only)
   * @param checkInId - Check-in ID
   * @param location - New parent location
   * @returns Updated check-in record
   */
  async updateParentLocation(checkInId: string, location: string): Promise<KidsClubCheckIn> {
    const response = await axiosInstance.put<KidsClubCheckInResponse>(
      `${this.baseUrl}/check-ins/${checkInId}/parent-location`,
      { location }
    );
    return response.data.data;
  }

  /**
   * Update check-in details (Staff only)
   * @param checkInId - Check-in ID
   * @param data - Fields to update (location, notes, timestamps, who checked in/out)
   * @returns Updated check-in record
   */
  async updateCheckIn(checkInId: string, data: {
    current_parent_location?: string;
    check_in_notes?: string;
    check_out_notes?: string;
    checked_in_by_member_id?: string;
    checked_out_by_member_id?: string;
    checked_in_at?: string;
    checked_out_at?: string;
  }): Promise<KidsClubCheckIn> {
    const response = await axiosInstance.put<KidsClubCheckInResponse>(
      `${this.baseUrl}/check-ins/${checkInId}`,
      data
    );
    return response.data.data;
  }

  // ==========================================
  // Emergency Alert Endpoints
  // ==========================================

  /**
   * Create emergency alert (Staff only)
   * @param data - Alert form data
   * @returns Created alert
   */
  async createAlert(data: EmergencyAlertFormData): Promise<EmergencyAlert> {
    const response = await axiosInstance.post<EmergencyAlertResponse>(
      `${this.baseUrl}/alerts`,
      data
    );
    return response.data.data;
  }

  /**
   * Acknowledge an alert (Member)
   * @param alertId - Alert ID
   * @param parentEta - Estimated time of arrival
   * @returns Updated alert
   */
  async acknowledgeAlert(alertId: string, parentEta?: string): Promise<EmergencyAlert> {
    const response = await axiosInstance.post<EmergencyAlertResponse>(
      `${this.baseUrl}/alerts/${alertId}/acknowledge`,
      { parent_eta: parentEta }
    );
    return response.data.data;
  }

  /**
   * Resolve an alert (Staff only)
   * @param alertId - Alert ID
   * @param resolutionNotes - Resolution notes
   * @returns Updated alert
   */
  async resolveAlert(alertId: string, resolutionNotes?: string): Promise<EmergencyAlert> {
    const response = await axiosInstance.post<EmergencyAlertResponse>(
      `${this.baseUrl}/alerts/${alertId}/resolve`,
      { resolution_notes: resolutionNotes }
    );
    return response.data.data;
  }

  /**
   * Get unresolved alerts (Staff only)
   * @returns List of unresolved alerts
   */
  async getUnresolvedAlerts(): Promise<EmergencyAlert[]> {
    const response = await axiosInstance.get<{ success: boolean; data: EmergencyAlert[] }>(
      `${this.baseUrl}/alerts/unresolved`
    );
    return response.data.data;
  }

  /**
   * Get resolved alerts for today (Staff only)
   * @returns List of today's resolved alerts
   */
  async getResolvedAlertsToday(): Promise<EmergencyAlert[]> {
    const response = await axiosInstance.get<{ success: boolean; data: EmergencyAlert[] }>(
      `${this.baseUrl}/alerts/resolved/today`
    );
    return response.data.data;
  }

  // ==========================================
  // Walk-In Endpoints
  // ==========================================

  /**
   * Lookup member by member number and get eligible dependents (Staff only)
   * @param memberNumber - Member number to lookup
   * @returns Member info with eligible dependents
   */
  async lookupMember(memberNumber: string): Promise<MemberLookupResult> {
    const response = await axiosInstance.get<{ success: boolean; data: MemberLookupResult }>(
      `${this.baseUrl}/members/lookup`,
      { params: { member_number: memberNumber } }
    );
    return response.data.data;
  }

  /**
   * Create a walk-in booking for existing member and check in immediately (Staff only)
   * @param data - Walk-in form data with member_id and dependent_id
   * @returns Walk-in booking and check-in result with capacity info
   */
  async createWalkIn(data: WalkInFormData): Promise<any> {
    const response = await axiosInstance.post(`${this.baseUrl}/walk-in/member`, data);
    return response.data.data;
  }

  // ==========================================
  // Date-Based Booking Endpoints
  // ==========================================

  /**
   * Get bookings for a specific date with summary statistics (Staff only)
   * @param date - Date in YYYY-MM-DD format (optional, defaults to today)
   * @param status - Status filter (optional)
   * @returns Bookings and summary statistics
   */
  async getBookingsByDate(date?: string, status?: string): Promise<{ summary: BookingSummary; data: KidsClubBooking[] }> {
    const params: any = {};
    if (date) params.date = date;
    if (status) params.status = status;
    const response = await axiosInstance.get(`${this.baseUrl}/bookings/date`, { params });
    return { summary: response.data.summary, data: response.data.data };
  }

  /**
   * Get booking summary for a date range (Staff only)
   * @param startDate - Start date in YYYY-MM-DD format (defaults to today)
   * @param endDate - End date in YYYY-MM-DD format (defaults to start + 7 days)
   * @param includeDetails - Include booking details (default: false)
   * @returns Summary data for each day in range
   */
  async getBookingsSummary(startDate?: string, endDate?: string, includeDetails = false): Promise<any> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (includeDetails) params.include_details = 'true';
    const response = await axiosInstance.get<{ success: boolean; data: any }>(
      `${this.baseUrl}/bookings/summary`,
      { params }
    );
    return response.data.data.summary; // Extract the summary array
  }

  // ==========================================
  // Capacity Settings Endpoints
  // ==========================================

  /**
   * Get Kids Club capacity settings (Staff only)
   * @returns Current settings
   */
  async getCapacitySettings(): Promise<KidsClubSettings> {
    const response = await axiosInstance.get<{ success: boolean; data: KidsClubSettings }>(
      `${this.baseUrl}/capacity/settings`
    );
    return response.data.data;
  }

  /**
   * Update Kids Club capacity settings (Admin only)
   * @param data - Settings to update
   * @returns Updated settings
   */
  async updateCapacitySettings(data: Partial<KidsClubSettings>): Promise<KidsClubSettings> {
    const response = await axiosInstance.put<{ success: boolean; data: KidsClubSettings }>(
      `${this.baseUrl}/capacity/settings`,
      data
    );
    return response.data.data;
  }

  /**
   * Update weekly schedule configuration (Admin only)
   * Each day has its own capacity and time slot settings
   * @param weeklySchedule - Weekly schedule configuration
   * @returns Updated settings
   */
  async updateWeeklySchedule(weeklySchedule: KidsClubSettings['weekly_schedule']): Promise<KidsClubSettings> {
    const response = await axiosInstance.put<{ success: boolean; data: KidsClubSettings }>(
      `${this.baseUrl}/capacity/settings/weekly-schedule`,
      weeklySchedule
    );
    return response.data.data;
  }

  /**
   * Create capacity override for a specific date (Admin only)
   * @param data - Override data
   * @returns Created override
   */
  async createCapacityOverride(data: Partial<CapacityOverride>): Promise<CapacityOverride> {
    const response = await axiosInstance.post<{ success: boolean; data: CapacityOverride }>(
      `${this.baseUrl}/capacity/override`,
      data
    );
    return response.data.data;
  }

  /**
   * Update capacity override (Admin only)
   * @param overrideId - Override ID
   * @param data - Updated override data
   * @returns Updated override
   */
  async updateCapacityOverride(overrideId: string, data: Partial<CapacityOverride>): Promise<CapacityOverride> {
    const response = await axiosInstance.put<{ success: boolean; data: CapacityOverride }>(
      `${this.baseUrl}/capacity/override/${overrideId}`,
      data
    );
    return response.data.data;
  }

  /**
   * Get capacity overrides for a date range (Admin only)
   * @param startDate - Start date (YYYY-MM-DD)
   * @param endDate - End date (YYYY-MM-DD)
   * @returns List of capacity overrides
   */
  async getCapacityOverrides(startDate: string, endDate: string): Promise<CapacityOverride[]> {
    const response = await axiosInstance.get<{ success: boolean; data: CapacityOverride[] }>(
      `${this.baseUrl}/capacity/overrides`,
      { params: { start_date: startDate, end_date: endDate } }
    );
    return response.data.data;
  }

  /**
   * Delete a capacity override (Admin only)
   * @param overrideId - Override ID
   * @returns Success status
   */
  async deleteCapacityOverride(overrideId: string): Promise<void> {
    await axiosInstance.delete(`${this.baseUrl}/capacity/override/${overrideId}`);
  }
}

const kidsClubService = new KidsClubService();
export default kidsClubService;
